import { ActionIcon, Group, Stack, Text, Tooltip } from "@mantine/core";
import { IconCheck, IconPencil, IconX } from "@tabler/icons-react";
import React, { useEffect, useState } from "react";
import { McInputNew, McTextAreaNew } from "..";
import { formatInputDate } from "../../app/routes/admin/selectors";

type TitleWithValueInputType = React.InputHTMLAttributes<HTMLInputElement>["type"] | "textarea";

const TitleWithValue: React.FC<{
  title: string;
  value: any;
  saveCb?: (oldValue: any, newValue: any) => void;
  type?: TitleWithValueInputType;
  pattern?: string;
  editable?: boolean;
  allowEmpty?: boolean;
  formatter?: any;
  item?: any;
  placeRight?: boolean;
  wrap?: boolean;
  hideIcon?: boolean;
  className?: string;
}> = ({
  title,
  value,
  saveCb,
  type,
  pattern,
  editable = false,
  allowEmpty = false,
  formatter,
  item,
  placeRight = false,
  wrap = false,
  hideIcon = false,
  className,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [viewValue, setViewValue] = useState("");

  const startEdit = () => {
    if (editable !== true) {
      return;
    }

    setEditValue(value);
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditValue(value);
    setEditMode(false);
  };

  const saveEdit = () => {
    if (allowEmpty === false && String(editValue ?? "").trim() === "") {
      return;
    }

    if (editValue !== value) {
      saveCb?.(value, editValue);
    }

    setEditMode(false);
  };

  const onEditorKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      cancelEdit();
      return;
    }

    if (e.key === "Enter" && type !== "textarea") {
      e.preventDefault();
      saveEdit();
    }
  };

  useEffect(() => {
    if (value instanceof Date) {
      const _value: string = JSON.parse(JSON.stringify(value));
      const inputDate = formatInputDate(_value);
      const formatted = formatter == null ? inputDate : formatter(_value);

      setViewValue(formatted);
      setEditValue(inputDate);
      return;
    } else {
      setEditValue(value);
    }

    try {
      if (formatter != null) {
        const formatted = item != null ? formatter(value, item) : formatter(value);
        setViewValue(formatted);
      } else {
        setViewValue(value);
      }
    } catch (error) {
      console.log(title);
      console.log(value);
      console.error(error);
    }
  }, [value, formatter, item, title]);

  const canSave = allowEmpty === true || String(editValue ?? "").trim() !== "";

  return (
    <div className={className}>
      <Stack gap={4}>
        {title != null && (
          <Text size="sm" fw={600}>
            {title}
          </Text>
        )}

        {editMode ? (
          <Group
            gap="xs"
            align={type === "textarea" ? "flex-start" : "center"}
            wrap={wrap ? "wrap" : "nowrap"}
            style={placeRight ? { flexDirection: "row-reverse", justifyContent: "flex-end" } : undefined}
          >
            {type === "textarea" ? (
              <McTextAreaNew
                value={editValue}
                onChange={(e) => setEditValue(e.currentTarget.value)}
                onKeyDown={onEditorKeyDown}
                autoFocus
                dynamicHeight
                style={{ minHeight: "150px", width: "100%" }}
              />
            ) : (
              <McInputNew
                pattern={pattern}
                type={type}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={onEditorKeyDown}
                autoFocus
                style={{ width: type === "date" ? "12em" : undefined, flex: 1 }}
              />
            )}

            {hideIcon !== true && (
              <>
                <Tooltip label="Lagre">
                  <ActionIcon variant="light" disabled={canSave === false} onClick={saveEdit}>
                    <IconCheck size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Avbryt">
                  <ActionIcon variant="subtle" color="gray" onClick={cancelEdit}>
                    <IconX size={16} />
                  </ActionIcon>
                </Tooltip>
              </>
            )}
          </Group>
        ) : (
          <Group
            gap="xs"
            align="center"
            wrap={wrap ? "wrap" : "nowrap"}
            style={placeRight ? { flexDirection: "row-reverse", justifyContent: "flex-end" } : undefined}
          >
            <Text>{viewValue || "-"}</Text>
            {editable === true && hideIcon !== true && (
              <Tooltip label="Rediger">
                <ActionIcon variant="subtle" onClick={startEdit}>
                  <IconPencil size={16} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        )}
      </Stack>
    </div>
  );
};
export default TitleWithValue;
